// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Konvo panel (the corner assistant opened). One request at a time: a starting
// point (Mail, Tasks, Calendar), a corner suggestion, a nudge or a typed ⌘K question opens
// it with an editable request, and running it sends the request to the Assistant DM with a
// small context line, so the DM stays the one place the conversation lives and "Continue in
// DM" is just navigation. The reply is mirrored here from the DM's messages as it streams,
// matched to the panel's own requests by message id (panelThread.ts). Every open, close and
// account switch starts a new session; async work from an older session is dropped.
// Without a connected assistant the panel shows the connect step instead, keeps the request
// and runs it as soon as the connection is made (here or anywhere else).

import {Routes} from '@app/app/Routes';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import Activity from '@app/features/konvo_activity/state/Activity';
import {isAssistantThinking} from '@app/features/konvo_assistant/state/AssistantThinking';
import {assistantChannelIdNow} from '@app/features/konvo_assistant/state/AssistantUnread';
import AssistantView from '@app/features/konvo_assistant/state/AssistantView';
import type {ClaudeLinkHolder} from '@app/features/konvo_connections/hooks/useClaudeLink';
import KonvoConnections from '@app/features/konvo_connections/state/KonvoConnections';
import type {PanelAbout} from '@app/features/konvo_panel/state/askAbout';
import {type LastChat, lastChat} from '@app/features/konvo_panel/state/lastChat';
import {panelThread} from '@app/features/konvo_panel/state/panelThread';
import type {QuickTask} from '@app/features/konvo_panel/state/quickTasks';
import StarterUsage from '@app/features/konvo_panel/state/StarterUsage';
import {
	isRangeKey,
	joinPeople,
	normalizeStarterContext,
	panelContextLine,
	panelMessage,
	RANGE_CHOICES,
	type RangeKey,
	rangeContext,
	type StarterContext,
	splitPeople,
	starterRequest,
	stripPanelContext,
} from '@app/features/konvo_panel/state/starters';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {getMaxAttachmentFileSize} from '@app/features/messaging/utils/AttachmentUtils';
import {sendFilesMessage} from '@app/features/messaging/utils/SendFilesMessage';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import ReadStates from '@app/features/read_state/state/ReadStates';
import Users from '@app/features/user/state/Users';
import Window from '@app/features/window/state/Window';
import {FLUXERBOT_ID} from '@fluxer/constants/src/AppConstants';
import type {Starter} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';
import {
	ASSISTANT_RESPONSE_PROVIDER,
	decodeAssistantResponseFields,
} from '@fluxer/schema/src/domains/konvo/AssistantResponse';
import type {MessageAttachment} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';
import {i18n} from '@lingui/core';
import {comparer, makeAutoObservable, reaction, runInAction} from 'mobx';

export type PanelSource =
	| 'starter'
	| 'suggestion'
	| 'ask'
	| 'nudge'
	| 'checklist'
	| 'dm_card'
	| 'corner'
	| 'composer'
	| 'message_menu';

export interface PanelOpenOptions {
	starter: Starter;
	/** Prefilled request (editable in the panel). Empty = the starter's default wording for the context. */
	request?: string;
	/** What the request is about (mail id, sender, domain, meeting id, …). See StarterContext for the keys. */
	context?: Record<string, string>;
	source: PanelSource;
	/** Page it was opened from (mail, tasks, calendar, home, chats, …) for tracking. */
	page?: string;
	/** Run straight away (default true). */
	run?: boolean;
	/** The corner suggestion this came from, if any. */
	suggestionId?: string;
	/** The nudge card this came from, if any (for panel.opened). */
	nudgeId?: string;
	/**
	 * A local flow that does the starter instead of an assistant run (the drafted task form,
	 * Draft with Konvo in the reply composer). Used when the panel opened only to connect:
	 * once connected, the panel closes and this runs.
	 */
	perform?: () => void;
	/**
	 * Phones: opened about one chat or message (the composer's sparkle, Ask Konvo on a message).
	 * The sheet shows it as a removable context row with its own starters (AskAboutSheet).
	 */
	about?: PanelAbout;
}

/** idle: request shown, not run; connect: no assistant yet; running: waiting on the reply. */
export type PanelPhase = 'idle' | 'connect' | 'connected' | 'sending' | 'running' | 'done' | 'failed';

/** Why a run failed: the request never reached the DM, or no answer came in time (it may still). */
export type PanelFailure = 'send' | 'timeout';

export type ConnectProvider = 'claude' | 'codex';

/** A file pasted or dropped into the panel, waiting to go with the next question. */
export interface PanelFile {
	key: string;
	file: File;
	/** A picture's thumbnail (object URL, revoked when the file leaves the panel). */
	previewUrl: string | null;
}

/** At most this many files go with one question, as in the Assistant DM. */
export const MAX_PANEL_FILES = 10;
let panelFileCounter = 0;

function revokePreviews(files: ReadonlyArray<PanelFile>): void {
	for (const file of files) if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
}

/** One turn of the panel conversation, read from the Assistant DM. */
export interface PanelTurn {
	message: Message;
	role: 'user' | 'assistant';
}

const ANSWER_TIMEOUT_MS = 120_000;

/** A brief Konvo posted on its own (never an answer to a question). */
function isBrief(message: Message): boolean {
	const embed = message.embeds.find((entry) => entry.provider?.name === ASSISTANT_RESPONSE_PROVIDER);
	return decodeAssistantResponseFields(embed?.fields)?.blocks.some((block) => block.type === 'brief') ?? false;
}

/** How many of the Assistant DM's messages an empty panel loads to find the last chat. */
const LAST_CHAT_HISTORY = 30;

/** Id props are kept up to this length by the activity allowlist. */
const ID_PROP_MAX = 400;

/** Meeting prep carries the calendar event id, so the server can skip that meeting's corner pop-up. */
function meetingProps(starter: Starter, context: StarterContext): {meeting_id?: string} {
	return starter === 'meeting_prep' && context.meeting_id ? {meeting_id: context.meeting_id.slice(0, ID_PROP_MAX)} : {};
}
const TICK_MS = 600;

/**
 * Whether the assistant that answers DM turns is ready: the selected provider (the server
 * routes every turn and suggestion to it) is linked and switched on. null while unknown.
 * The right-click suggestion flows use the same rule (AssistantSuggestions.available).
 */
export function assistantReady(): boolean | null {
	const claude = KonvoConnections.claude;
	const codex = KonvoConnections.codex;
	if (!claude && !codex) return null;
	const selected = KonvoConnections.assistantProvider === 'codex' ? codex : claude;
	return selected?.connected === true && selected.enabled === true;
}

class KonvoPanelStore {
	isOpen = false;
	/** Hidden but kept: the corner icon brings it back. */
	minimised = false;
	options: PanelOpenOptions | null = null;
	request = '';
	edited = false;
	range: RangeKey | null = null;
	people: Array<string> = [];
	phase: PanelPhase = 'idle';
	failure: PanelFailure | null = null;
	provider: ConnectProvider = 'claude';
	channelId: string | null = null;
	/** When the first request of this session was sent (ms); 0 before anything ran. */
	startedAt = 0;
	/** When the latest request or follow-up was sent (for timing only; replies are matched by id). */
	askedAt = 0;
	/** Ids of the messages this panel sent in the current thread (the first is the request). */
	requestIds: Array<string> = [];
	/** A follow-up is in flight: the thread shows it right away. */
	pendingFollowUp: string | null = null;
	/** Files pasted or dropped in, going with the next question. */
	files: Array<PanelFile> = [];
	/** The files of the follow-up in flight, shown with it. */
	pendingFiles: Array<PanelFile> = [];
	/** Some pasted files were left out: too big or too many. */
	filesRefused: 'too_big' | 'too_many' | null = null;
	/**
	 * The last chat was picked up again: its newest question, and the DM messages that came
	 * after it (Konvo's own briefs), which stay out of the thread. Until something is asked
	 * here, nothing in it is answering and nothing new is read.
	 */
	resumedRequestId: string | null = null;
	resumedLaterIds: ReadonlySet<string> = new Set();
	/** The chat or message the panel was opened about was removed: questions go without it. */
	aboutRemoved = false;
	/** The chat, email or task open under the panel; a typed free question carries it along. */
	onScreen: StarterContext = {};
	/** Bumped on every open, close and account switch; async work from an older session is dropped. */
	session = 0;
	/** The Claude sign-in link of this session's connect step. */
	claudeLink: {id: string; authUrl: string} | null = null;
	claudeLinkStarted = false;
	/** Something was sent to the DM in this session (Continue in DM must not send it again). */
	private sentInSession = false;
	/** What the last failed send tried, for Retry. */
	private failedSend: {content: string; followUp: string | null; files: Array<PanelFile>} | null = null;
	private openedAt = 0;
	private resultReported = true;
	private editReported = false;
	private timer: ReturnType<typeof setInterval> | null = null;
	private readonly logger = new Logger('KonvoPanel');

	constructor() {
		makeAutoObservable<this, 'timer' | 'logger'>(this, {timer: false, logger: false}, {autoBind: true});
		// Logging out or switching accounts: nothing of the previous person's panel survives.
		reaction(
			() => Users.currentUserId ?? null,
			(next, previous) => {
				if (next !== previous) this.reset();
			},
		);
		// Connected somewhere else (Settings, another tab, the corner) while the connect step shows.
		reaction(
			() => this.assistantConnected,
			(connected) => {
				if (connected === true && this.phase === 'connect' && (this.isOpen || this.minimised)) {
					void this.resumeAfterConnect(this.session);
				}
			},
		);
		// The replies show here, in the panel: they need no notification, and the DM counts them as read.
		reaction(
			() => ({channelId: this.channelId, requestId: this.answeringRequestId}),
			(next, previous) => {
				if (previous.channelId && previous.channelId !== next.channelId)
					AssistantView.setAnswering(previous.channelId, null);
				if (next.channelId) AssistantView.setAnswering(next.channelId, next.requestId);
			},
			{equals: comparer.structural},
		);
		reaction(
			() =>
				this.channelId && this.lastReplyId && this.askedHere && Window.focused && Window.visible
					? {channelId: this.channelId, messageId: this.lastReplyId}
					: null,
			(read) => {
				if (read) ReadStates.handleChannelAck(read);
			},
			{equals: comparer.structural},
		);
	}

	/** The request whose replies the open panel shows as they arrive; null while hidden or after another message. */
	get answeringRequestId(): string | null {
		if (!this.isOpen || !this.askedHere || !this.thread.answering) return null;
		return this.requestIds.at(-1) ?? null;
	}

	/** The latest request was sent from this panel (not a picked-up chat's). */
	private get askedHere(): boolean {
		const latest = this.requestIds.at(-1);
		return latest != null && latest !== this.resumedRequestId;
	}

	/** The newest reply the open panel shows. */
	get lastReplyId(): string | null {
		if (!this.isOpen) return null;
		return this.thread.turns.findLast((turn) => turn.role === 'assistant')?.message.id ?? null;
	}

	get starter(): Starter {
		return this.options?.starter ?? 'free';
	}

	get context(): StarterContext {
		return (this.options?.context ?? {}) as StarterContext;
	}

	get page(): string {
		return this.options?.page ?? 'other';
	}

	/** The chat or message the panel is about (null when it was not opened about one, or it was removed). */
	get about(): PanelAbout | null {
		return this.aboutRemoved ? null : (this.options?.about ?? null);
	}

	/** The assistant is linked and switched on; null while unknown. */
	get assistantConnected(): boolean | null {
		return assistantReady();
	}

	get rangeChoices(): ReadonlyArray<RangeKey> {
		return RANGE_CHOICES[this.starter];
	}

	get busy(): boolean {
		return this.phase === 'sending' || this.phase === 'running';
	}

	/** Opened without a starting point: a plain chat with one box at the bottom, no request block. */
	get freeChat(): boolean {
		return this.starter === 'free';
	}

	/** The request is asked (or kept for after connecting): a free chat shows it as the first bubble. */
	get requestAsked(): boolean {
		if (!this.request.trim()) return false;
		return this.startedAt > 0 || this.phase === 'sending' || this.phase === 'connect' || this.phase === 'connected';
	}

	/** Something ran in this session: the corner icon can show a dot while minimised. */
	get hasSession(): boolean {
		return this.options != null && this.startedAt > 0;
	}

	private get thread() {
		void Messages.version;
		if (!this.channelId || this.requestIds.length === 0) return {turns: [], latestReplies: [], answering: false};
		const later = this.resumedLaterIds;
		const messages = ChannelMessages.get(this.channelId)?.toArray() ?? [];
		return panelThread(
			later.size > 0 ? messages.filter((message) => !later.has(message.id)) : messages,
			this.requestIds,
			FLUXERBOT_ID,
		);
	}

	/** The files sent with the first request (the request block shows them). */
	get requestAttachments(): ReadonlyArray<MessageAttachment> {
		void Messages.version;
		const first = this.requestIds[0];
		if (!this.channelId || !first) return [];
		return ChannelMessages.get(this.channelId)?.get(first)?.attachments ?? [];
	}

	/** The last chat in the Assistant DM, while an empty panel could pick it up; null otherwise. */
	get lastChat(): LastChat<Message> | null {
		if (!this.isOpen || !this.freeChat || this.startedAt > 0 || this.phase !== 'idle' || this.request.trim())
			return null;
		if (this.options?.about) return null;
		void Messages.version;
		const channelId = assistantChannelIdNow();
		const messages = channelId ? ChannelMessages.get(channelId) : null;
		if (!messages?.ready) return null;
		return lastChat(messages.toArray(), FLUXERBOT_ID, isBrief);
	}

	/** The panel shows a chat picked up from the DM (not one asked in this session). */
	get resumed(): boolean {
		return this.resumedRequestId != null;
	}

	/** The panel conversation: follow-ups and replies after the first request (shown as the request block). */
	get turns(): Array<PanelTurn> {
		return this.thread.turns;
	}

	/** Assistant messages that answered the latest request. */
	get latestReplies(): Array<Message> {
		return this.thread.latestReplies;
	}

	get dmRoute(): string | null {
		return this.channelId ? Routes.dmChannel(this.channelId) : null;
	}

	/** Kept per session, so a remount (minimise, restore) reuses the link instead of starting another. */
	get claudeKeeper(): ClaudeLinkHolder {
		const session = this.session;
		return {
			link: this.claudeLink,
			started: this.claudeLinkStarted,
			markStarted: () => {
				if (session === this.session) this.setClaudeLinkState(this.claudeLink, true);
			},
			setLink: (link) => {
				if (session === this.session) this.setClaudeLinkState(link, true);
				else if (link) KonvoConnections.cancelClaudeLink();
			},
		};
	}

	private setClaudeLinkState(link: {id: string; authUrl: string} | null, started: boolean): void {
		this.claudeLink = link;
		this.claudeLinkStarted = started;
	}

	/** Ends the session: in-flight sends, refreshes and connect callbacks see a new number and stop. */
	private nextSession(): number {
		this.stopTimer();
		// An unused sign-in link of this session is abandoned with it (never another tab's: the
		// panel only cancels when it made one itself).
		if (this.claudeLink) KonvoConnections.cancelClaudeLink();
		this.claudeLink = null;
		this.claudeLinkStarted = false;
		this.session += 1;
		return this.session;
	}

	open(input: PanelOpenOptions): void {
		if (this.isOpen || this.minimised) this.reportClosed();
		const session = this.nextSession();
		const options = {...input, context: normalizeStarterContext(input.starter, input.context)};
		const context = options.context as StarterContext;
		this.options = options;
		this.request = options.request?.trim() || starterRequest(i18n, options.starter, context);
		this.edited = false;
		this.editReported = false;
		this.range = isRangeKey(context.range) ? context.range : (RANGE_CHOICES[options.starter][0] ?? null);
		if (options.starter === 'client_needs' && !isRangeKey(context.range)) this.range = 'last_90_days';
		this.people = splitPeople(context.people);
		this.aboutRemoved = false;
		this.phase = 'idle';
		this.failure = null;
		this.channelId = null;
		this.clearThread();
		this.sentInSession = false;
		this.openedAt = Date.now();
		this.isOpen = true;
		this.minimised = false;
		if (options.starter === 'free' && !options.about) void this.loadLastChat(session);
		Activity.track('panel.opened', {
			starter: options.starter,
			page: this.page,
			source: options.source,
			...(options.suggestionId ? {suggestion_id: options.suggestionId.slice(0, ID_PROP_MAX)} : {}),
			...(options.nudgeId ? {nudge_id: options.nudgeId.slice(0, ID_PROP_MAX)} : {}),
			...meetingProps(options.starter, context),
		});
		void this.begin(session, options.run !== false);
	}

	/** An empty panel offers the last chat: load the Assistant DM's recent messages when they are not yet. */
	private async loadLastChat(session: number): Promise<void> {
		const channelId = assistantChannelIdNow();
		if (!channelId || ChannelMessages.get(channelId)?.ready) return;
		try {
			await MessageCommands.fetchMessages(channelId, null, null, LAST_CHAT_HISTORY);
		} catch (error) {
			if (session === this.session) this.logger.warn('Assistant DM history unavailable', error);
		}
	}

	/** Pick the last chat up again: it shows as this panel's conversation and what is typed follows it up. */
	resumeLastChat(): void {
		const chat = this.lastChat;
		const channelId = assistantChannelIdNow();
		if (!chat || !channelId || !this.options) return;
		Activity.track('panel.action_used', {starter: this.starter, action: 'last_chat'});
		this.channelId = channelId;
		this.requestIds = chat.requestIds;
		this.request = stripPanelContext(chat.question.content);
		this.edited = false;
		this.startedAt = chat.question.timestamp.getTime();
		this.askedAt = this.startedAt;
		this.resumedRequestId = chat.requestIds.at(-1) ?? null;
		this.resumedLaterIds = new Set(chat.laterIds);
		// It is in the DM already: Continue in DM only goes there.
		this.sentInSession = true;
		this.phase = 'done';
	}

	/** Start over in the open panel: an empty chat (the last one stays in the DM). */
	newChat(): void {
		if (!this.isOpen || this.busy) return;
		Activity.track('panel.action_used', {starter: this.starter, action: 'new_chat'});
		this.reportResult(false);
		this.options = {...(this.options ?? {source: 'corner'}), starter: 'free', context: {}, about: undefined};
		this.request = '';
		this.edited = false;
		this.phase = 'idle';
		this.failure = null;
		this.channelId = null;
		this.clearThread();
		this.clearFiles();
		this.sentInSession = false;
	}

	/** Pasted or dropped files: they wait above the box until the next question takes them. */
	addFiles(list: ReadonlyArray<File>): void {
		this.filesRefused = null;
		const maxBytes = getMaxAttachmentFileSize();
		for (const file of list) {
			if (file.size > maxBytes) {
				this.filesRefused = 'too_big';
				continue;
			}
			if (this.files.length >= MAX_PANEL_FILES) {
				this.filesRefused = 'too_many';
				break;
			}
			panelFileCounter += 1;
			this.files.push({
				key: `panel-file-${panelFileCounter}`,
				file,
				previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
			});
		}
	}

	removeFile(key: string): void {
		revokePreviews(this.files.filter((entry) => entry.key === key));
		this.files = this.files.filter((entry) => entry.key !== key);
		this.filesRefused = null;
	}

	/** The staged files, handed to a send (which owns their thumbnails from then on). */
	private takeFiles(): Array<PanelFile> {
		const files = this.files;
		this.files = [];
		this.filesRefused = null;
		return files;
	}

	private clearFiles(): void {
		revokePreviews(this.files);
		this.files = [];
		this.filesRefused = null;
	}

	private clearThread(): void {
		this.resumedRequestId = null;
		this.resumedLaterIds = new Set();
		this.startedAt = 0;
		this.askedAt = 0;
		this.requestIds = [];
		this.pendingFollowUp = null;
		revokePreviews(this.pendingFiles);
		this.pendingFiles = [];
		if (this.failedSend) revokePreviews(this.failedSend.files);
		this.failedSend = null;
		this.resultReported = true;
	}

	/** A starting point was clicked: record it, then do it here or open the panel. */
	startFrom(
		starter: Starter,
		options: {context?: Record<string, string>; page: string; perform?: () => void; request?: string},
	): void {
		const connected = this.assistantConnected;
		Activity.track('starter.clicked', {
			starter,
			page: options.page,
			locked: connected !== true,
			...meetingProps(starter, (options.context ?? {}) as StarterContext),
		});
		StarterUsage.markUsed(starter);
		if (options.perform && connected === true) {
			options.perform();
			return;
		}
		this.open({
			starter,
			context: options.context,
			request: options.request,
			source: 'starter',
			page: options.page,
			perform: options.perform,
		});
	}

	private async begin(session: number, run: boolean): Promise<void> {
		if (this.assistantConnected == null) {
			try {
				await KonvoConnections.refreshAssistant();
			} catch (error) {
				this.logger.warn('Assistant status unavailable', error);
			}
		}
		if (session !== this.session || !this.isOpen) return;
		if (this.assistantConnected !== true) {
			runInAction(() => {
				this.phase = 'connect';
			});
			Activity.track('connect.step', {step: 'opened', source: 'panel', starter: this.starter});
			return;
		}
		// Status was unknown at click time: the local flow (task form, reply composer) does it, not a DM run.
		const perform = this.options?.perform;
		if (perform) {
			this.close();
			perform();
			return;
		}
		if (run && this.request.trim()) await this.run();
	}

	close(): void {
		if (!this.isOpen && !this.minimised) return;
		this.reportClosed();
		this.nextSession();
		this.isOpen = false;
		this.minimised = false;
		this.options = null;
		this.phase = 'idle';
		this.failure = null;
		this.channelId = null;
		this.clearThread();
		this.clearFiles();
		this.sentInSession = false;
	}

	/** Logged out or another account: drop everything without reporting it for the new one. */
	private reset(): void {
		this.nextSession();
		this.onScreen = {};
		this.isOpen = false;
		this.minimised = false;
		this.options = null;
		this.request = '';
		this.people = [];
		this.range = null;
		this.phase = 'idle';
		this.failure = null;
		this.channelId = null;
		this.clearFiles();
		this.clearThread();
		this.sentInSession = false;
	}

	/** Hide without losing the conversation; `restore` brings it back. */
	minimise(): void {
		if (!this.isOpen) return;
		this.isOpen = false;
		this.minimised = true;
	}

	restore(): void {
		if (!this.minimised || !this.options) return;
		this.isOpen = true;
		this.minimised = false;
	}

	private reportClosed(): void {
		if (!this.options) return;
		// A run still waiting when the panel goes did not give a result here.
		this.reportResult(false);
		Activity.track('panel.closed', {
			starter: this.starter,
			seconds: Math.round((Date.now() - this.openedAt) / 1000),
		});
	}

	setRequest(text: string): void {
		this.request = text;
		this.edited = true;
		if (!this.editReported) {
			this.editReported = true;
			Activity.track('panel.request_edited', {starter: this.starter});
		}
	}

	setRange(range: RangeKey): void {
		if (this.range === range) return;
		this.range = range;
		this.edited = true;
		if (!this.editReported) {
			this.editReported = true;
			Activity.track('panel.request_edited', {starter: this.starter});
		}
	}

	removePerson(name: string): void {
		this.people = this.people.filter((person) => person !== name);
		this.edited = true;
		if (!this.editReported) {
			this.editReported = true;
			Activity.track('panel.request_edited', {starter: this.starter});
		}
	}

	setProvider(provider: ConnectProvider): void {
		if (this.provider === provider) return;
		this.provider = provider;
		Activity.track('connect.step', {step: 'provider_chosen', provider, source: 'panel'});
	}

	setOnScreen(context: StarterContext): void {
		this.onScreen = context;
	}

	/** The x on the context row: what is asked next goes without the chat (only before anything ran). */
	removeAbout(): void {
		if (!this.options?.about || this.startedAt > 0 || this.busy) return;
		this.aboutRemoved = true;
		this.options = {...this.options, context: {}};
	}

	private contextLine(): string | null {
		if (this.options?.about && this.aboutRemoved) return null;
		let base = this.context;
		if (this.starter === 'free' && Object.keys(base).length === 0) {
			// A typed question: say what was open when it was asked, so "this" and "he" resolve.
			if (Object.keys(this.onScreen).length === 0) return null;
			base = {...this.onScreen, on_screen: 'yes'};
		}
		const context: StarterContext = {
			...base,
			people: this.people.length > 0 ? joinPeople(this.people) : undefined,
		};
		return panelContextLine(this.starter, context, {
			range: this.range ? rangeContext(this.range, new Date()) : undefined,
		});
	}

	/** Send the request (again) to the Assistant DM and follow the reply here. */
	async run(): Promise<void> {
		const request = this.request.trim();
		if (!request || this.busy) return;
		if (this.assistantConnected !== true) {
			this.phase = 'connect';
			return;
		}
		const rerun = this.startedAt > 0;
		const files = this.takeFiles();
		// The previous run (if any) ends here: a new thread starts with this request.
		this.reportResult(false);
		this.clearThread();
		this.edited = false;
		const sent = await this.send(panelMessage(request, this.contextLine()), null, files);
		if (sent && rerun) this.logger.info('Re-ran the panel request');
	}

	/**
	 * A quick task from the empty panel: a starting point runs as one (its local flow, such as
	 * the drafted-task form, replaces the panel), anything else is asked here with its context.
	 */
	quickTask(task: QuickTask, perform?: () => void): void {
		if (this.busy || this.startedAt > 0) return;
		Activity.track('panel.action_used', {starter: this.starter, action: `quick.${task.id.split(':')[0]}`});
		if (task.starter) {
			if (perform && this.assistantConnected === true) this.close();
			this.startFrom(task.starter, {context: task.context as Record<string, string>, page: this.page, perform});
			return;
		}
		if (!task.request || !this.options) return;
		this.options = {...this.options, context: task.context as Record<string, string>};
		this.request = task.request;
		this.edited = false;
		void this.run();
	}

	/** A follow-up in the same conversation (typed, or an action on the result). */
	async followUp(text: string, action?: string): Promise<void> {
		const question = text.trim();
		// Typed follow-ups take the pasted files along (and may be only files); actions never do.
		const withFiles = !action && this.files.length > 0;
		if ((!question && !withFiles) || this.busy || this.requestIds.length === 0) return;
		if (action) Activity.track('panel.action_used', {starter: this.starter, action});
		else Activity.track('panel.followup', {starter: this.starter});
		this.reportResult(false);
		await this.send(question, question, withFiles ? this.takeFiles() : []);
	}

	/** Send what failed to send again. */
	async retry(): Promise<void> {
		const failed = this.failedSend;
		if (!failed || this.busy) return;
		if (failed.followUp == null) {
			// The request goes again as typed; its files wait in the box again.
			this.files = [...failed.files, ...this.files];
			this.failedSend = null;
			await this.run();
			return;
		}
		this.failedSend = null;
		await this.send(failed.content, failed.followUp, failed.files);
	}

	/** Sends one message of this session; false when it failed or the session moved on meanwhile. */
	private async send(content: string, followUp: string | null, files: Array<PanelFile> = []): Promise<boolean> {
		const session = this.session;
		this.phase = 'sending';
		this.failure = null;
		this.failedSend = null;
		this.pendingFollowUp = followUp;
		// Shown with the question until the DM has it (a first request shows them in its block).
		this.pendingFiles = files;
		this.sentInSession = true;
		try {
			const channelId = this.channelId ?? (await this.ensureChannel());
			if (session !== this.session) return false;
			if (!channelId) throw new Error('no assistant channel');
			if (!ChannelMessages.get(channelId)?.ready) {
				await MessageCommands.fetchMessages(channelId, null, null, 30);
				if (session !== this.session) return false;
			}
			const askedAt = Date.now();
			runInAction(() => {
				this.channelId = channelId;
				this.askedAt = askedAt;
			});
			const nonce = SnowflakeUtils.fromTimestamp(askedAt);
			const message =
				files.length > 0
					? await sendFilesMessage(
							channelId,
							files.map((entry) => entry.file),
							content,
							nonce,
						)
					: await MessageCommands.send(channelId, {content, nonce});
			if (session !== this.session) return false;
			if (!message?.id) throw new Error('message not sent');
			runInAction(() => {
				this.requestIds = [...this.requestIds, message.id];
				if (!this.startedAt) this.startedAt = askedAt;
				this.pendingFollowUp = null;
				// The sent message shows the uploaded files from here on.
				revokePreviews(this.pendingFiles);
				this.pendingFiles = [];
				this.phase = 'running';
				this.resultReported = false;
			});
			this.startTimer();
			return true;
		} catch (error) {
			if (session !== this.session) return false;
			this.logger.warn('Panel request failed', error);
			runInAction(() => {
				this.phase = 'failed';
				this.failure = 'send';
				this.failedSend = {content, followUp, files};
				this.pendingFollowUp = null;
				this.pendingFiles = [];
			});
			// Nothing reached the DM: the turn failed for sure.
			this.resultReported = false;
			this.reportResult(false);
			return false;
		}
	}

	private async ensureChannel(): Promise<string | null> {
		try {
			const channel = await PrivateChannelCommands.ensureDMChannelResponse(FLUXERBOT_ID);
			return channel?.id ?? null;
		} catch (error) {
			this.logger.warn('Assistant channel unavailable', error);
			return null;
		}
	}

	private startTimer(): void {
		this.stopTimer();
		this.timer = setInterval(() => this.tick(), TICK_MS);
	}

	private stopTimer(): void {
		if (this.timer) clearInterval(this.timer);
		this.timer = null;
	}

	/**
	 * Done once a reply arrived and the assistant stopped working. Two minutes of silence
	 * while it is not working shows "no answer yet", but the panel keeps watching: a late
	 * reply still turns it into a result.
	 */
	tick(now: number = Date.now()): void {
		const waiting = this.phase === 'running' || (this.phase === 'failed' && this.failure === 'timeout');
		if (!waiting || !this.channelId) {
			this.stopTimer();
			return;
		}
		const working = isAssistantThinking(this.channelId, now);
		const replied = this.latestReplies.length > 0;
		if (replied && !working) {
			this.phase = 'done';
			this.failure = null;
			this.stopTimer();
			this.reportResult(true);
			return;
		}
		if (this.phase === 'running' && !replied && !working && now - this.askedAt > ANSWER_TIMEOUT_MS) {
			this.phase = 'failed';
			this.failure = 'timeout';
		}
	}

	/** One panel.result per turn: ok when the answer showed here, not ok when it failed or was left. */
	private reportResult(ok: boolean): void {
		if (this.resultReported) return;
		this.resultReported = true;
		Activity.track('panel.result', {
			starter: this.starter,
			ms: this.askedAt ? Date.now() - this.askedAt : 0,
			ok,
		});
	}

	/** An action on the result: sent as a follow-up. */
	useAction(prompt: string, action: string): void {
		void this.followUp(prompt, action);
	}

	/** A source chip or card was opened. */
	noteSourceOpened(): void {
		Activity.track('panel.action_used', {starter: this.starter, action: 'open_source'});
	}

	/** A local action on the result (Put in reply, say), for the funnel. */
	noteAction(action: string): void {
		Activity.track('panel.action_used', {starter: this.starter, action});
	}

	/** Move the conversation to the Assistant DM. */
	async continueInDm(): Promise<void> {
		Activity.track('panel.to_dm', {starter: this.starter});
		// Not run yet: take the request (with its context line) along so the DM has it. Once
		// anything was sent in this session (also a run still in flight) it is already there.
		const request = this.request.trim();
		const content =
			!this.sentInSession && request && this.assistantConnected === true
				? panelMessage(request, this.contextLine())
				: null;
		const known = this.channelId;
		this.close();
		const channelId = known ?? (await this.ensureChannel());
		if (!channelId) return;
		RouterUtils.transitionTo(Routes.dmChannel(channelId));
		if (content) {
			await MessageCommands.send(channelId, {content, nonce: SnowflakeUtils.fromTimestamp(Date.now())});
		}
	}

	/** The connect step finished: run the kept request (or the local flow it stood in for). */
	async connected(provider: ConnectProvider): Promise<void> {
		const session = this.session;
		Activity.track('connect.step', {step: 'connected', provider, source: 'panel'});
		try {
			await KonvoConnections.refreshAssistant();
		} catch (error) {
			this.logger.warn('Assistant status refresh failed', error);
		}
		await this.resumeAfterConnect(session);
	}

	/** Once per connect step: whichever notices the connection first (the sign-in, or the status) runs it. */
	private async resumeAfterConnect(session: number): Promise<void> {
		if (session !== this.session || this.phase !== 'connect') return;
		if (!this.isOpen && !this.minimised) return;
		// The link was used (or is no longer needed): nothing to cancel on close.
		this.claudeLink = null;
		const perform = this.options?.perform;
		if (perform) {
			this.close();
			perform();
			return;
		}
		this.phase = 'connected';
		await this.run();
	}

	/** "Not now": close, and say so for the funnel. */
	notNow(): void {
		Activity.track('connect.step', {step: 'closed', source: 'panel'});
		this.close();
	}
}

export default new KonvoPanelStore();
